import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Chip,
  Typography,
  Button,
  Paper,
  Alert,
  Skeleton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  PageLayout,
  PaginationBar,
  TWButton,
} from "../../../shared/components";
import {
  colors,
  typography,
  textFieldStyles,
} from "../../../shared/styles/theme";
import {
  getSession,
  getClauses,
  postAssess,
  postAddClause,
  getCategories,
} from "../utils/contractApi";
import type {
  CategoryInfo,
  ContractSession,
  ClauseInfo,
  ContractUploadResponse,
  ClausePosition,
} from "../types/contracts";
import ClauseCard, { ClauseCardSkeleton } from "./ClauseCard";
import ContractSummary from "./ContractSummary";
import ContractReviewProgressStepper, {
  type ContractReviewProgressActiveStep,
} from "./ContractReviewProgressStepper";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const POSITION_FILTERS: { value: ClausePosition | null; label: string }[] = [
  { value: null, label: "All" },
  { value: "IDEAL", label: "Ideal" },
  { value: "ACCEPTABLE", label: "Acceptable" },
  { value: "NON_NEGOTIABLE", label: "Non-Negotiable" },
  { value: "UNCLASSIFIED", label: "Unclassified" },
];

const POSITION_BUTTON_STYLE: Record<
  ClausePosition,
  {
    activeBorder: string;
    activeBg: string;
    activeColor: string;
    iconColor: string;
  }
> = {
  IDEAL: {
    activeBorder: "#2e7d32",
    activeBg: "rgba(46, 125, 50, 0.1)",
    activeColor: "#2e7d32",
    iconColor: "#2e7d32",
  },
  ACCEPTABLE: {
    activeBorder: "#1565c0",
    activeBg: "rgba(21, 101, 192, 0.1)",
    activeColor: "#1565c0",
    iconColor: "#1565c0",
  },
  NON_NEGOTIABLE: {
    activeBorder: "#c62828",
    activeBg: "rgba(198, 40, 40, 0.1)",
    activeColor: "#c62828",
    iconColor: "#c62828",
  },
  UNCLASSIFIED: {
    activeBorder: "#616161",
    activeBg: "rgba(97, 97, 97, 0.1)",
    activeColor: "#616161",
    iconColor: "#616161",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Compliance Health card (sidebar widget)
// ─────────────────────────────────────────────────────────────────────────────

interface ComplianceHealthCardProps {
  clauseSummary: import("../types/contracts").ClauseSummary | null;
  totalClauses: number;
  isSigned: boolean;
  assessing: boolean;
  assessmentDone: boolean;
  onRunAssessment: () => void;
}

const ComplianceHealthCard: React.FC<ComplianceHealthCardProps> = ({
  clauseSummary,
  totalClauses,
  isSigned,
  assessing,
  assessmentDone,
  onRunAssessment,
}) => {
  const ideal = clauseSummary?.ideal ?? 0;
  const acceptable = clauseSummary?.acceptable ?? 0;
  const nonNegotiable = clauseSummary?.non_negotiable ?? 0;
  const unclassified = clauseSummary?.unclassified ?? 0;

  return (
    <Box sx={{ width: 268, flexShrink: 0, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          border: `2px dashed rgba(72, 161, 173, 0.45)`,
          borderRadius: 2,
          backgroundColor: "rgba(240, 246, 255, 0.55)",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
        }}
      >
        {/* Stats */}
        <Box sx={{ width: "100%" }}>
          {/* Signed / Draft status */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
            <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.secondary }}>Status</Typography>
            <Chip
              label={isSigned ? "Signed" : "Draft"}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 600,
                backgroundColor: isSigned ? "rgba(46, 125, 50, 0.1)" : "rgba(21, 108, 134, 0.1)",
                color: isSigned ? "#2e7d32" : colors.primary,
                border: `1px solid ${isSigned ? "rgba(46, 125, 50, 0.3)" : "rgba(21, 108, 134, 0.3)"}`,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>
          <Divider sx={{ mb: 1.25 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
            <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.secondary }}>Total Clauses</Typography>
            <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary }}>{totalClauses}</Typography>
          </Box>
          {ideal > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.secondary }}>Ideal</Typography>
              <Chip
                label={`${ideal} CLAUSES`}
                size="small"
                sx={{
                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                  color: "#2e7d32",
                  fontSize: "0.65rem",
                  fontWeight: typography.fontWeight.bold,
                  height: 20,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
          )}
          {acceptable > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.fontSize.sm,
                  color: colors.text.secondary,
                }}
              >
                Acceptable
              </Typography>
              <Chip
                label={`${acceptable} CLAUSES`}
                size="small"
                sx={{
                  backgroundColor: "rgba(21, 108, 134, 0.1)",
                  color: colors.primary,
                  fontSize: "0.65rem",
                  fontWeight: typography.fontWeight.bold,
                  height: 20,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
          )}
          {nonNegotiable > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.fontSize.sm,
                  color: colors.text.secondary,
                }}
              >
                Non-Negotiable
              </Typography>
              <Chip
                label={`${nonNegotiable} CLAUSES`}
                size="small"
                sx={{
                  backgroundColor: "rgba(198, 40, 40, 0.1)",
                  color: "#c62828",
                  fontSize: "0.65rem",
                  fontWeight: typography.fontWeight.bold,
                  height: 20,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
          )}
          {unclassified > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.fontSize.sm,
                  color: colors.text.secondary,
                }}
              >
                Unclassified
              </Typography>
              <Chip
                label={`${unclassified} CLAUSES`}
                size="small"
                sx={{
                  backgroundColor: "rgba(97, 97, 97, 0.1)",
                  color: "#616161",
                  fontSize: "0.65rem",
                  fontWeight: typography.fontWeight.bold,
                  height: 20,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Run AI Assessment */}
        <TWButton
          label={assessing ? "Assessing…" : "Run AI Assessment"}
          onClick={onRunAssessment}
          loading={assessing}
          disabled={assessing || assessmentDone}
          sx={{ width: "100%" }}
        />
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ContractReviewSessionPage: React.FC = () => {
  const { sessionUuid } = useParams<{ sessionUuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const uploadState = location.state as {
    uploadResult?: ContractUploadResponse;
  } | null;

  const [session, setSession] = useState<ContractSession | null>(null);
  const [clauses, setClauses] = useState<ClauseInfo[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [useUploadClauses, setUseUploadClauses] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<ClausePosition | null>(
    null,
  );
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [manualFilter, setManualFilter] = useState<boolean | null>(null);
  const [billableFilter, setBillableFilter] = useState<boolean | null>(null);
  const [reviewedFilter, setReviewedFilter] = useState<boolean | null>(null);
  const [assessmentSnackOpen, setAssessmentSnackOpen] = useState(false);
  const [addClauseOpen, setAddClauseOpen] = useState(false);
  const [addClauseSubmitting, setAddClauseSubmitting] = useState(false);
  const [addClauseError, setAddClauseError] = useState<string | null>(null);
  const [addClauseForm, setAddClauseForm] = useState({
    original_text: "",
    section_name: "",
    paragraph_reference: "",
  });
  const [addClauseSnackOpen, setAddClauseSnackOpen] = useState(false);
  // Tracks whether AI assessment has ever been run for this session.
  // Once true, we never regress below step 3, even if a single clause
  // category is manually edited (which temporarily drops assessedCount).
  const [hasEverAssessed, setHasEverAssessed] = useState(false);

  const loadSessionAndClauses = useCallback(async () => {
    if (!sessionUuid) return;
    setLoading(true);
    setError(null);
    try {
      const [sessionRes, clausesRes] = await Promise.all([
        getSession(sessionUuid).catch(() => null),
        getClauses(sessionUuid, {
          page: 1,
          page_size: pageSize,
          category: categoryFilter ?? undefined,
          is_manual: manualFilter ?? undefined,
          billable_flag: billableFilter ?? undefined,
          reviewed: reviewedFilter ?? undefined,
        }).catch(() => null),
      ]);
      if (sessionRes) setSession(sessionRes);
      if (clausesRes) {
        setClauses(clausesRes.clauses);
        setTotalItems(clausesRes.total_items);
        setUseUploadClauses(false);
      } else if (uploadState?.uploadResult?.clauses?.length) {
        setClauses(uploadState.uploadResult.clauses);
        setTotalItems(uploadState.uploadResult.clauses.length);
        setUseUploadClauses(true);
        if (!sessionRes)
          setSession({
            session_uuid: sessionUuid,
            user_id: "",
            client_name: uploadState.uploadResult.message || "Contract",
            contract_type: null,
            is_signed: uploadState.uploadResult.is_signed ?? false,
            ai_summary: null,
            final_summary: null,
            total_clauses: uploadState.uploadResult.clauses_count,
            clause_summary: null,
            created_at: "",
            updated_at: "",
          });
      } else {
        setClauses([]);
        setTotalItems(0);
        if (!sessionRes)
          setError(
            "Session not found. You may have arrived from an upload; try uploading again.",
          );
      }
    } catch (e) {
      setError("Failed to load session or clauses.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [
    sessionUuid,
    pageSize,
    categoryFilter,
    manualFilter,
    billableFilter,
    reviewedFilter,
    uploadState?.uploadResult,
  ]);

  useEffect(() => {
    loadSessionAndClauses();
  }, [loadSessionAndClauses]);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => {
        /* degrade gracefully */
      });
  }, []);

  const handlePageChange = useCallback(
    async (_: unknown, newPage: number) => {
      if (useUploadClauses) {
        setPage(newPage);
        return;
      }
      if (!sessionUuid) return;
      setLoading(true);
      try {
        const res = await getClauses(sessionUuid, {
          page: newPage + 1,
          page_size: pageSize,
          category: categoryFilter ?? undefined,
          is_manual: manualFilter ?? undefined,
          billable_flag: billableFilter ?? undefined,
          reviewed: reviewedFilter ?? undefined,
        });
        setClauses(res.clauses);
        setTotalItems(res.total_items);
        setPage(newPage);
      } catch (e) {
        setError("Failed to load clauses.");
      } finally {
        setLoading(false);
      }
    },
    [
      sessionUuid,
      pageSize,
      categoryFilter,
      manualFilter,
      billableFilter,
      reviewedFilter,
      useUploadClauses,
    ],
  );

  const handleRowsPerPageChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize);
      setPage(0);
      if (!useUploadClauses && sessionUuid) {
        setLoading(true);
        getClauses(sessionUuid, {
          page: 1,
          page_size: newSize,
          category: categoryFilter ?? undefined,
          is_manual: manualFilter ?? undefined,
          billable_flag: billableFilter ?? undefined,
          reviewed: reviewedFilter ?? undefined,
        })
          .then((res) => {
            setClauses(res.clauses);
            setTotalItems(res.total_items);
          })
          .catch(() => setError("Failed to load clauses."))
          .finally(() => setLoading(false));
      }
    },
    [
      sessionUuid,
      categoryFilter,
      manualFilter,
      billableFilter,
      reviewedFilter,
      useUploadClauses,
    ],
  );

  const handlePaginationPageChange = useCallback(
    (nextPageOneBased: number) => {
      void handlePageChange(null as never, nextPageOneBased - 1);
    },
    [handlePageChange],
  );

  const filteredClausesForDisplay = useUploadClauses
    ? categoryFilter ||
      manualFilter != null ||
      billableFilter != null ||
      reviewedFilter != null
      ? clauses.filter((c) => {
          const matchCategory =
            !categoryFilter ||
            (c.human_position ?? c.position) === categoryFilter;
          const matchManual =
            manualFilter == null || c.is_manual === manualFilter;
          const matchBillable =
            billableFilter == null || c.billable_flag === billableFilter;
          const matchReviewed =
            reviewedFilter == null ||
            (reviewedFilter ? c.reviewed_by != null : c.reviewed_by == null);
          return matchCategory && matchManual && matchBillable && matchReviewed;
        })
      : clauses
    : clauses;

  const totalItemsForDisplay = useUploadClauses
    ? filteredClausesForDisplay.length
    : totalItems;
  const displayClauses = useUploadClauses
    ? filteredClausesForDisplay.slice(
        page * pageSize,
        page * pageSize + pageSize,
      )
    : clauses;

  const clauseTotalPages =
    totalItemsForDisplay > 0
      ? Math.max(1, Math.ceil(totalItemsForDisplay / pageSize))
      : 1;

  const handleCategoryFilterClick = useCallback(
    (value: ClausePosition | null) => {
      const next = categoryFilter === value ? null : value;
      setCategoryFilter(next);
      setPage(0);
    },
    [categoryFilter],
  );

  const handleManualFilterClick = useCallback(
    (value: boolean | null) => {
      const next = manualFilter === value ? null : value;
      setManualFilter(next);
      setPage(0);
    },
    [manualFilter],
  );

  const handleBillableFilterClick = useCallback(() => {
    setBillableFilter((prev) => (prev === true ? null : true));
    setPage(0);
  }, []);

  const handleReviewedFilterClick = useCallback(() => {
    setReviewedFilter((prev) => (prev === true ? null : true));
    setPage(0);
  }, []);

  const handleRunAssessment = useCallback(async () => {
    if (!sessionUuid) return;
    setAssessing(true);
    setError(null);
    try {
      await postAssess(sessionUuid);
      setHasEverAssessed(true);
      await loadSessionAndClauses();
      setAssessmentSnackOpen(true);
    } catch (e) {
      setError("Assessment failed. Please try again.");
      console.error(e);
    } finally {
      setAssessing(false);
    }
  }, [sessionUuid, loadSessionAndClauses]);

  const handleClauseUpdated = useCallback(
    (updated: ClauseInfo) => {
      if (useUploadClauses) {
        setClauses((prev) =>
          prev.map((c) =>
            c.clause_uuid === updated.clause_uuid ? updated : c,
          ),
        );
        return;
      }
      if (!sessionUuid) return;
      setLoading(true);

      const refreshClauses = getClauses(sessionUuid, {
        page: page + 1,
        page_size: pageSize,
        category: categoryFilter ?? undefined,
        is_manual: manualFilter ?? undefined,
        billable_flag: billableFilter ?? undefined,
        reviewed: reviewedFilter ?? undefined,
      });

      // Refresh the session when the clause needs to be re-assessed:
      // - text edited → category_tag is null
      // - category manually changed → category_tag is set but position is null
      const refreshSession =
        updated.position === null
          ? getSession(sessionUuid).catch(() => null)
          : Promise.resolve(null);

      Promise.all([refreshClauses, refreshSession])
        .then(([clausesRes, sessionRes]) => {
          setClauses(clausesRes.clauses);
          setTotalItems(clausesRes.total_items);
          if (sessionRes) setSession(sessionRes);
        })
        .catch(() => setError("Failed to refresh clauses."))
        .finally(() => setLoading(false));
    },
    [
      sessionUuid,
      page,
      pageSize,
      categoryFilter,
      manualFilter,
      billableFilter,
      reviewedFilter,
      useUploadClauses,
    ],
  );

  const handleSummaryUpdated = useCallback(
    (updated: Pick<ContractSession, "ai_summary" | "final_summary">) => {
      setSession((prev) =>
        prev
          ? {
              ...prev,
              ai_summary: updated.ai_summary,
              final_summary: updated.final_summary,
            }
          : null,
      );
    },
    [],
  );

  const openAddClause = useCallback(() => {
    setAddClauseForm({
      original_text: "",
      section_name: "",
      paragraph_reference: "",
    });
    setAddClauseError(null);
    setAddClauseOpen(true);
  }, []);

  const closeAddClause = useCallback(() => {
    if (!addClauseSubmitting) setAddClauseOpen(false);
  }, [addClauseSubmitting]);

  const handleAddClauseSubmit = useCallback(async () => {
    const text = addClauseForm.original_text?.trim();
    if (!text) {
      setAddClauseError("Clause text is required.");
      return;
    }
    if (!sessionUuid) return;
    setAddClauseSubmitting(true);
    setAddClauseError(null);
    try {
      await postAddClause(sessionUuid, {
        original_text: text,
        section_name: addClauseForm.section_name?.trim() || undefined,
        paragraph_reference:
          addClauseForm.paragraph_reference?.trim() || undefined,
      });
      setAddClauseOpen(false);
      setAddClauseSnackOpen(true);
      await loadSessionAndClauses();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail
          : null;
      setAddClauseError(
        typeof msg === "string" ? msg : "Failed to add clause.",
      );
    } finally {
      setAddClauseSubmitting(false);
    }
  }, [sessionUuid, addClauseForm, loadSessionAndClauses]);

  const handleClauseDeleted = useCallback(
    (clauseUuid: string) => {
      if (useUploadClauses) {
        setClauses((prev) => prev.filter((c) => c.clause_uuid !== clauseUuid));
        setTotalItems((n) => Math.max(0, n - 1));
        return;
      }
      loadSessionAndClauses();
    },
    [useUploadClauses, loadSessionAndClauses],
  );

  // Initialise hasEverAssessed from session data so refreshes / page reloads
  // don't regress the step back to 2 when some clauses have been assessed.
  useEffect(() => {
    if (!session || hasEverAssessed) return;
    const clauseSummary = session.clause_summary;
    const assessedCount = clauseSummary
      ? clauseSummary.ideal +
        clauseSummary.acceptable +
        clauseSummary.non_negotiable +
        clauseSummary.unclassified
      : 0;
    if (assessedCount > 0) setHasEverAssessed(true);
  }, [session, hasEverAssessed]);

  const progressActiveStep = useMemo((): ContractReviewProgressActiveStep => {
    if (!session) return 2;
    const total = session.total_clauses ?? totalItems;
    const clauseSummary = session.clause_summary;
    const assessedCount = clauseSummary
      ? clauseSummary.ideal +
        clauseSummary.acceptable +
        clauseSummary.non_negotiable +
        clauseSummary.unclassified
      : 0;
    const hasAssessmentRun = total === 0 || assessedCount >= total;
    const hasSummary = !!(session.ai_summary || session.final_summary);
    if (hasSummary) return 4;
    // Stay at step 3 if assessing, all clauses are assessed, OR assessment
    // has ever been run (prevents regression when a category is manually edited).
    if (assessing || hasAssessmentRun || hasEverAssessed) return 3;
    return 2;
  }, [session, totalItems, assessing, hasEverAssessed]);

  const headerRightActions = (
    <Button
      variant="text"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate("/contract-review")}
      sx={{
        color: colors.text.primary,
        textTransform: "none",
        fontSize: typography.fontSize.base,
        fontWeight: 500,
        "&:hover": { backgroundColor: colors.background.hover },
      }}
    >
      Back to Contract Review
    </Button>
  );

  return (
    <PageLayout rightActions={headerRightActions}>
      <Box sx={{ width: "100%", mx: "auto" }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !session && !clauses.length ? (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: 3 }}>
              <Skeleton
                variant="text"
                width={320}
                height={36}
                sx={{ mb: 1, bgcolor: "rgba(0,0,0,0.06)" }}
              />
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Skeleton
                  variant="rounded"
                  width={100}
                  height={24}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={24}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
              </Box>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2,
                backgroundColor: colors.background.card,
                border: `1px solid ${colors.border.light}`,
                borderRadius: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton
                  variant="circular"
                  width={32}
                  height={32}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
                <Skeleton
                  variant="text"
                  width={180}
                  height={28}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
                <Skeleton
                  variant="rounded"
                  width={90}
                  height={24}
                  sx={{ ml: 1, bgcolor: "rgba(0,0,0,0.06)" }}
                />
              </Box>
            </Paper>
            <Box
              sx={{
                py: 2,
                mb: 2,
                borderBottom: `1px solid ${colors.border.light}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Skeleton
                  variant="text"
                  width={220}
                  height={28}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
                <Skeleton
                  variant="rounded"
                  width={180}
                  height={36}
                  sx={{ bgcolor: "rgba(0,0,0,0.06)" }}
                />
              </Box>
            </Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <ClauseCardSkeleton key={i} />
            ))}
          </Box>
        ) : (
          <>
            {session && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: typography.fontFamily.heading,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.text.primary,
                    fontSize: typography.fontSize["2xl"],
                    mb: 0.5,
                  }}
                >
                  Review: {session.client_name || "Contract"}
                  {session.contract_type ? ` – ${session.contract_type}` : ""}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                </Box>
                <ContractReviewProgressStepper
                  activeStep={progressActiveStep}
                  isSigned={session?.is_signed ?? false}
                />
              </Box>
            )}
            {session &&
              sessionUuid &&
              (() => {
                const total = session.total_clauses ?? totalItems;
                const summary = session.clause_summary;
                const assessedCount = summary
                  ? summary.ideal +
                    summary.acceptable +
                    summary.non_negotiable +
                    summary.unclassified
                  : 0;
                const hasAssessmentRun =
                  total === 0 || assessedCount >= total;
                const assessmentDone = total > 0 && assessedCount >= total;

                const complianceCard = (
                  <ComplianceHealthCard
                    clauseSummary={session.clause_summary}
                    totalClauses={total}
                    isSigned={session.is_signed}
                    assessing={assessing}
                    assessmentDone={assessmentDone}
                    onRunAssessment={handleRunAssessment}
                  />
                );

                // At step 2 nothing shown here (info is inline in the clauses header)
                if (progressActiveStep < 3) return null;

                return (
                  <Box sx={{ display: "flex", gap: 3, alignItems: "stretch", mb: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                      <ContractSummary
                        session={session}
                        sessionUuid={sessionUuid}
                        clauseCount={total}
                        hasAssessmentRun={hasAssessmentRun || hasEverAssessed}
                        isSigned={session.is_signed}
                        sx={{ height: "100%", mb: 0 }}
                        onSummaryUpdated={handleSummaryUpdated}
                      />
                    </Box>
                    {complianceCard}
                  </Box>
                );
              })()}
            {session && (
              <Box
                sx={{
                  backgroundColor: colors.background.page,
                  py: 2,
                  px: 0,
                  mb: 2,
                  borderBottom: `1px solid ${colors.border.light}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: (session.total_clauses ?? 0) > 0 ? 2 : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: typography.fontFamily.heading,
                        fontWeight: typography.fontWeight.bold,
                        color: colors.text.primary,
                        fontSize: "1.25rem",
                      }}
                    >
                      Extracted Security Clauses
                    </Typography>
                    {/* Inline status + count at step 2 */}
                    {progressActiveStep < 3 && (
                      <>
                        <Chip
                          label={session.is_signed ? "Signed" : "Draft"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            backgroundColor: session.is_signed ? "rgba(46, 125, 50, 0.1)" : "rgba(21, 108, 134, 0.1)",
                            color: session.is_signed ? "#2e7d32" : colors.primary,
                            border: `1px solid ${session.is_signed ? "rgba(46, 125, 50, 0.3)" : "rgba(21, 108, 134, 0.3)"}`,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        <Typography variant="body2" sx={{ color: colors.text.secondary, whiteSpace: "nowrap" }}>
                          {session.total_clauses ?? totalItems} clause{(session.total_clauses ?? totalItems) === 1 ? "" : "s"}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {sessionUuid && !useUploadClauses && progressActiveStep < 3 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                      <Button
                        variant="outlined"
                        onClick={openAddClause}
                        sx={{
                          borderColor: colors.border.medium,
                          color: colors.text.primary,
                          textTransform: "none",
                          "&:hover": {
                            borderColor: colors.primary,
                            backgroundColor: "rgba(21, 108, 134, 0.04)",
                          },
                        }}
                      >
                        Add clause
                      </Button>
                      {totalItems > 0 && (() => {
                        const clauseSum = session.clause_summary;
                        const clauseTotal = session.total_clauses ?? totalItems;
                        const assessed = clauseSum
                          ? clauseSum.ideal + clauseSum.acceptable + clauseSum.non_negotiable + clauseSum.unclassified
                          : 0;
                        const done = clauseTotal > 0 && assessed >= clauseTotal;
                        return (
                          <TWButton
                            label={assessing ? "Assessing…" : "Run AI Assessment"}
                            onClick={handleRunAssessment}
                            loading={assessing}
                            disabled={assessing || done}
                            sx={{ minWidth: 180 }}
                          />
                        );
                      })()}
                    </Box>
                  )}
                </Box>

                {(session.total_clauses ?? 0) > 0 &&
                  progressActiveStep >= 3 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "nowrap",
                        alignItems: "center",
                        minWidth: 0,
                        overflowX: "auto",
                        py: 0.5,
                      }}
                    >
                      {POSITION_FILTERS.map(({ value, label }) => {
                        const isActive = categoryFilter === value;
                        const style = value
                          ? POSITION_BUTTON_STYLE[value]
                          : null;
                        const activeBorder =
                          style?.activeBorder ?? colors.primaryHover;
                        const activeBg =
                          style?.activeBg ?? "rgba(71, 161, 173, 0.1)";
                        const activeColor = style?.activeColor ?? "#156C86";
                        return (
                          <Button
                            key={label}
                            variant="outlined"
                            onClick={() => handleCategoryFilterClick(value)}
                            sx={{
                              minHeight: "36px",
                              minWidth: "fit-content",
                              flexShrink: 0,
                              px: 2,
                              border: isActive
                                ? `1.5px solid ${activeBorder}`
                                : "1px solid #d9dede",
                              backgroundColor: isActive ? activeBg : "#ffffff",
                              color: isActive ? activeColor : "#333333",
                              textTransform: "none",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              borderRadius: "40px",
                              boxShadow: "none",
                              "&:hover": {
                                border: `1.5px solid ${activeBorder}`,
                                backgroundColor: activeBg,
                                color: activeColor,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {label}
                          </Button>
                        );
                      })}
                      {[{ value: true, label: "Manually added" }].map(
                        ({ value, label }) => {
                          const isActive = manualFilter === value;
                          const activeBorder = colors.primaryHover;
                          const activeBg = "rgba(21, 108, 134, 0.1)";
                          const activeColor = colors.primary;
                          return (
                            <Button
                              key={label}
                              variant="outlined"
                              onClick={() => handleManualFilterClick(value)}
                              sx={{
                                minHeight: "36px",
                                minWidth: "fit-content",
                                flexShrink: 0,
                                px: 2,
                                border: isActive
                                  ? `1.5px solid ${activeBorder}`
                                  : "1px solid #d9dede",
                                backgroundColor: isActive
                                  ? activeBg
                                  : "#ffffff",
                                color: isActive ? activeColor : "#333333",
                                textTransform: "none",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                borderRadius: "40px",
                                boxShadow: "none",
                                "&:hover": {
                                  border: `1.5px solid ${activeBorder}`,
                                  backgroundColor: activeBg,
                                  color: activeColor,
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {label}
                            </Button>
                          );
                        },
                      )}

                      {/* Billable filter */}
                      <Button
                        variant="outlined"
                        onClick={handleBillableFilterClick}
                        sx={{
                          minHeight: "36px",
                          minWidth: "fit-content",
                          flexShrink: 0,
                          px: 2,
                          border: billableFilter
                            ? "1.5px solid #e65100"
                            : "1px solid #d9dede",
                          backgroundColor: billableFilter
                            ? "rgba(230, 81, 0, 0.08)"
                            : "#ffffff",
                          color: billableFilter ? "#e65100" : "#333333",
                          textTransform: "none",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          borderRadius: "40px",
                          boxShadow: "none",
                          "&:hover": {
                            border: "1.5px solid #e65100",
                            backgroundColor: "rgba(230, 81, 0, 0.08)",
                            color: "#e65100",
                            boxShadow: "none",
                          },
                        }}
                      >
                        Billable
                      </Button>

                      {/* Approved filter */}
                      <Button
                        variant="outlined"
                        onClick={handleReviewedFilterClick}
                        sx={{
                          minHeight: "36px",
                          minWidth: "fit-content",
                          flexShrink: 0,
                          px: 2,
                          border: reviewedFilter
                            ? "1.5px solid #2e7d32"
                            : "1px solid #d9dede",
                          backgroundColor: reviewedFilter
                            ? "rgba(46, 125, 50, 0.08)"
                            : "#ffffff",
                          color: reviewedFilter ? "#2e7d32" : "#333333",
                          textTransform: "none",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          borderRadius: "40px",
                          boxShadow: "none",
                          "&:hover": {
                            border: "1.5px solid #2e7d32",
                            backgroundColor: "rgba(46, 125, 50, 0.08)",
                            color: "#2e7d32",
                            boxShadow: "none",
                          },
                        }}
                      >
                        Approved
                      </Button>
                    </Box>
                  )}
              </Box>
            )}

            {loading ? (
              <>
                {Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                  <ClauseCardSkeleton key={i} />
                ))}
              </>
            ) : displayClauses.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: colors.background.card,
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No clauses to display.
                </Typography>
              </Paper>
            ) : (
              <>
                {displayClauses.map((clause, idx) => (
                  <ClauseCard
                    key={clause.clause_uuid}
                    clause={clause}
                    index={page * pageSize + idx}
                    isSigned={session?.is_signed ?? false}
                    isAssessing={assessing}
                    categories={categories}
                    onClauseUpdated={handleClauseUpdated}
                    onClauseDeleted={
                      !useUploadClauses ? handleClauseDeleted : undefined
                    }
                  />
                ))}
                {totalItemsForDisplay > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${colors.border.light}`,
                    }}
                  >
                    <PaginationBar
                      page={page + 1}
                      totalPages={clauseTotalPages}
                      onChange={handlePaginationPageChange}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}

        <Snackbar
          open={assessmentSnackOpen}
          autoHideDuration={4000}
          onClose={() => setAssessmentSnackOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            onClose={() => setAssessmentSnackOpen(false)}
          >
            Assessment complete.
          </Alert>
        </Snackbar>

        <Snackbar
          open={addClauseSnackOpen}
          autoHideDuration={4000}
          onClose={() => setAddClauseSnackOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            onClose={() => setAddClauseSnackOpen(false)}
          >
            Clause added.
          </Alert>
        </Snackbar>

        <Dialog
          open={addClauseOpen}
          onClose={closeAddClause}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add clause manually</DialogTitle>
          <DialogContent>
            {addClauseError && (
              <Alert
                severity="error"
                onClose={() => setAddClauseError(null)}
                sx={{ mb: 2 }}
              >
                {addClauseError}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add a security clause that was missed by the AI (e.g. from another
              section or document).
            </Typography>
            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="Clause text"
              value={addClauseForm.original_text}
              onChange={(e) =>
                setAddClauseForm((f) => ({
                  ...f,
                  original_text: e.target.value,
                }))
              }
              placeholder="Paste or type the clause text…"
              sx={{ mb: 2, ...textFieldStyles }}
            />
            <TextField
              fullWidth
              label="Section name (optional)"
              value={addClauseForm.section_name}
              onChange={(e) =>
                setAddClauseForm((f) => ({
                  ...f,
                  section_name: e.target.value,
                }))
              }
              placeholder="e.g. Data Security"
              sx={{ mb: 2, ...textFieldStyles }}
            />
            <TextField
              fullWidth
              label="Paragraph reference (optional)"
              value={addClauseForm.paragraph_reference}
              onChange={(e) =>
                setAddClauseForm((f) => ({
                  ...f,
                  paragraph_reference: e.target.value,
                }))
              }
              placeholder="e.g. 4.2.1"
              sx={{ ...textFieldStyles }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={closeAddClause}
              disabled={addClauseSubmitting}
              sx={{ color: colors.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddClauseSubmit}
              disabled={addClauseSubmitting}
              sx={{
                backgroundColor: colors.primary,
                "&:hover": { backgroundColor: colors.primaryHover },
              }}
            >
              {addClauseSubmitting ? "Adding…" : "Add clause"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageLayout>
  );
};

export default ContractReviewSessionPage;
